import { useEffect, useRef, useState } from "react";
import { logUndoEvent, api } from "@/lib/api";

// Iter 56 — module-level cache of the extend-threshold config so every
// chip doesn't re-fetch. Populated lazily on the first render; admins can
// update it via the analytics-card slider (which calls setExtendThreshold).
let EXTEND_THRESHOLD_SECONDS = 2;
let EXTEND_THRESHOLD_LOADED = false;

export function setExtendThreshold(seconds) {
  const n = Number(seconds);
  if (Number.isFinite(n) && n >= 0 && n <= 5) {
    EXTEND_THRESHOLD_SECONDS = Math.round(n);
    EXTEND_THRESHOLD_LOADED = true;
  }
}

async function ensureExtendThresholdLoaded() {
  if (EXTEND_THRESHOLD_LOADED) return EXTEND_THRESHOLD_SECONDS;
  try {
    const r = await api.get("/undo-extend-config");
    const n = Number(r.data?.threshold_seconds);
    if (Number.isFinite(n)) EXTEND_THRESHOLD_SECONDS = Math.round(n);
  } catch { /* keep default */ }
  EXTEND_THRESHOLD_LOADED = true;
  return EXTEND_THRESHOLD_SECONDS;
}

/**
 * Iter 51 — a tiny "auto-dismisses in Xs" countdown surface for sonner toasts
 * that carry an ``action: { label: "Undo" }`` handler.
 *
 * Passed into ``toast.*({ description: <UndoCountdownChip /> })`` so users
 * know exactly how long they have to click Undo before the toast disappears.
 * Ticks once per 250ms and shows a monospace "Auto-dismiss in Xs" chip so
 * the drift is legible without stealing focus.
 *
 * Iter 52 — pause on hover: attaches mouseenter/mouseleave listeners to the
 * closest ``[data-sonner-toast]`` ancestor so hovering anywhere on the toast
 * pauses (matching sonner's own auto-dismiss pause behaviour).
 *
 * Iter 53 — auto-extend on late hover: if the user hovers with < 2s
 * remaining, the chip bumps the visible countdown by 3s (once per toast
 * instance) so they don't accidentally miss the click while reading. The
 * extension is one-shot and flagged via ``data-extended`` so QA/accessibility
 * layers can surface it.
 */
export default function UndoCountdownChip({ duration = 8000, kind }) {
  const [remaining, setRemaining] = useState(Math.ceil(duration / 1000));
  const [paused, setPaused] = useState(false);
  const [extended, setExtended] = useState(false);
  const pausedRef = useRef(false);
  const rootRef = useRef(null);
  const extendedRef = useRef(false); // one-shot latch — never extend twice
  // Iter 56 — snapshot the admin-tunable threshold on mount so every chip
  // instance uses a stable value even if the admin bumps it mid-toast.
  const thresholdRef = useRef(EXTEND_THRESHOLD_SECONDS);
  useEffect(() => {
    ensureExtendThresholdLoaded().then((t) => { thresholdRef.current = t; });
  }, []);

  useEffect(() => {
    // Tick every 250ms so the seconds display updates smoothly even if the
    // user is watching the toast animate out. Rounding up so the chip
    // never shows "0s" while the toast is still visible.
    const t = setInterval(() => {
      if (pausedRef.current) return;
      setRemaining((r) => {
        const next = r - 0.25;
        return next <= 0 ? 0 : next;
      });
    }, 250);
    return () => clearInterval(t);
  }, []);

  // Iter 52 — hover freeze + Iter 53 auto-extend. Attach listeners to the
  // closest sonner toast wrapper so hovering anywhere on the toast pauses
  // (not just directly on the chip).
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const host = el.closest("[data-sonner-toast]") || el.parentElement;
    if (!host) return;
    const onEnter = () => {
      pausedRef.current = true;
      setPaused(true);
      // Iter 53 — one-shot extension when the user is racing the clock.
      // Read `remaining` via the functional setter to avoid stale-closure
      // issues (the effect only ever runs once at mount).
      if (!extendedRef.current) {
        setRemaining((r) => {
          // Iter 56 — dynamic threshold. 0 disables the auto-extend
          // entirely (admins can turn it off from the analytics card).
          const t = thresholdRef.current;
          if (t > 0 && r > 0 && r <= t) {
            extendedRef.current = true;
            setExtended(true);
            // Iter 55 — analytics: fire-and-forget so we know how often
            // the ≤2s hover-late auto-extend actually saves the user.
            // Only logged when we know the parent's ``kind`` — chips
            // rendered without a kind (defensive) are silent.
            if (kind) logUndoEvent("extended", kind);
            return r + 3;
          }
          return r;
        });
      }
    };
    const onLeave = () => {
      pausedRef.current = false;
      setPaused(false);
    };
    host.addEventListener("mouseenter", onEnter);
    host.addEventListener("mouseleave", onLeave);
    return () => {
      host.removeEventListener("mouseenter", onEnter);
      host.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const s = Math.ceil(remaining);
  return (
    <span
      ref={rootRef}
      data-testid="undo-countdown-chip"
      data-paused={paused ? "true" : "false"}
      data-extended={extended ? "true" : "false"}
      className="mono text-[10px] uppercase tracking-widest text-zinc-500"
    >
      {paused ? (
        <>
          <span className="text-amber-400">Paused</span>
          {extended && <span className="text-[#00C805]"> · +3s</span>}
          {" · "}{s}s remaining
        </>
      ) : (
        <>
          Auto-dismiss in <span className="text-zinc-300 tabular-nums">{s}s</span>
          {extended && <span className="text-[#00C805]"> · +3s</span>}
        </>
      )}
    </span>
  );
}
